import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import { useGetTourSalesforceInventory } from '~/hooks/apis/tours/useTourSalesforceInventory';

import NativeTourCard from './NativeTourCard';

interface Props {
  legacyTourId: string;
}

const SALESFORCE_DATE = 'DD-MM-YYYY';
const LOW_INVENTORY = 5;

const formatDate = (date: string) => dayjs(date, SALESFORCE_DATE).format('D MMM YYYY');
const formatPrice = (price: number, currency: string) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price);

function inventoryChip(left: number) {
  if (left <= 0) {
    return <Chip size="small" color="error" label="Sold out" />;
  }
  return (
    <Chip
      size="small"
      color={left <= LOW_INVENTORY ? 'warning' : 'success'}
      variant="outlined"
      label={`${left} left`}
    />
  );
}

/** What Salesforce has left to sell, live: each option's departures and their room types. Read only. */
export default function NativeTourSalesforceInventory({ legacyTourId }: Props) {
  const {
    data: inventory = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetTourSalesforceInventory(`tour-${legacyTourId}`);
  const [optionId, setOptionId] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);

  const option = inventory.find((candidate) => candidate.variationId === optionId) ?? inventory[0];
  const departures = useMemo(() => {
    const today = dayjs().startOf('day');
    return [...(option?.departures ?? [])]
      .filter((departure) => showPast || !dayjs(departure.endDate, SALESFORCE_DATE).isBefore(today))
      .sort((a, b) => dayjs(a.startDate, SALESFORCE_DATE).diff(dayjs(b.startDate, SALESFORCE_DATE)));
  }, [option, showPast]);

  if (isLoading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Couldn't load Salesforce's inventory: {error.message}
      </Alert>
    );
  }
  if (inventory.length === 0) {
    return <Alert severity="info">Salesforce has no inventory for this tour.</Alert>;
  }

  const soldOut = departures.filter((departure) => departure.inventoryLeft <= 0).length;

  return (
    <NativeTourCard
      title="Salesforce inventory"
      description="Live from Salesforce, where it's managed. Use it to check the new model's inventory against."
    >
      <Stack spacing={2}>
        {inventory.length > 1 && (
          <Tabs
            value={option.variationId}
            onChange={(_event, next: string) => setOptionId(next)}
            variant="scrollable"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {inventory.map((candidate) => (
              <Tab key={candidate.variationId} value={candidate.variationId} label={candidate.variationName} />
            ))}
          </Tabs>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Typography color="text.secondary">
            {departures.length} departure{departures.length === 1 ? '' : 's'}
            {soldOut > 0 ? `, ${soldOut} sold out` : ''}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={showPast} onChange={(event) => setShowPast(event.target.checked)} />}
              label="Show past departures"
            />
            <Button variant="text" disabled={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          </Stack>
        </Stack>
        {departures.length === 0 ? (
          <Typography color="text.secondary">No upcoming departures.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Departure</TableCell>
                  <TableCell>Left</TableCell>
                  <TableCell>Room types</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departures.map((departure) => (
                  <TableRow key={`${departure.startDate}-${departure.endDate}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{formatDate(departure.startDate)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        to {formatDate(departure.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>{inventoryChip(departure.inventoryLeft)}</TableCell>
                    <TableCell>
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        {departure.purchasableOptions.map((room) => (
                          <Chip
                            key={room.roomType}
                            size="small"
                            variant="outlined"
                            color={room.inventoryLeft <= 0 ? 'error' : 'default'}
                            label={`${room.roomType} · ${formatPrice(room.price, room.currency)} · ${
                              room.inventoryLeft <= 0 ? 'sold out' : `${room.inventoryLeft} left`
                            }`}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Stack>
    </NativeTourCard>
  );
}
